import {
    AmfTierContractTerms,
    CoreRate,
    CoreRates,
    ModifyCoreRatesInput,
    PaperPlanMetadata,
    PrimeContractTerms,
    ProgramType,
    PublishContractInput,
    RatchetDefinitionsEnum,
    RatchetModelDefinitionEnum,
    RatchetModels,
    RatchetModelsEnum,
    RateTiers,
    RoyaltyModelType,
    RoyaltyModelTypesEnum,
    RoyaltyModelsEnum,
    SetPrimeContractTermsInput,
    TerritoryCodeList,
    TerritoryTermInput,
    TerritoryTermInputs,
    TieredRates,
    TieredRatesList
} from '@amzn/coral_com-amazon-musicpapercontractconfigurationservice';
import { UnknownCoralError } from '@amzn/ihm-js-coral-client';

import { isEmpty, isNull, isNumber, isUndefined, sortBy, uniqBy } from 'lodash';
import { contractConfigurationClient } from 'src/api/client';
import { wmgBlendedRateGridColIdSuffix } from 'src/constants/rateCards';
import { ROUTES } from 'src/constants/routes';
import { IMetadata } from 'src/types/IMetadata';
import {
    IAmfContractTerms,
    IPrimeAMFRateCardValues,
    IPrimeContractTerms,
    IRateCardGridRowData
} from 'src/types/IRateCard';
import { Handlers } from 'src/util/SaveManager/SaveManager';
import { getThresholdFieldName } from 'src/util/thresholdUtil';

import {
    COMBINED_PRIME_UMG_FLOOR_CONVERSION_MODELS,
    conversionModelsThresholdsMap
} from '../AddContractTerms/AddConversionModel/conversionModelThresholds';

export interface PrimeAMFRateCardSaveProps {
    contractId: number;
    user: string;
    values: IPrimeAMFRateCardValues;
    metadata: IMetadata;
    customerTypes: PaperPlanMetadata[];
    programTypes: ProgramType[];
    conversionModels: RatchetModels;
}

const createPrimeContractTerms = (contractTerms: IPrimeContractTerms): PrimeContractTerms => {
    return PrimeContractTerms.clone({
        costPerPlay: {
            playTypes: contractTerms.costPerPlay!.playTypes,
            programTypeList: contractTerms.costPerPlay!.programTypeList,
            customerTypes: contractTerms.costPerPlay?.customerTypes,
            conversions: contractTerms.costPerPlay?.conversions?.map((conversion, index) => ({
                ...conversion,
                orderId: index + 1
            }))
        },
        monthlyActiveUser: {
            customerType: contractTerms.monthlyActiveUser?.customerType
        },
        prsDeductionCap: {
            type: contractTerms.prsDeductionCap!.type,
            value: Number(contractTerms.prsDeductionCap!.value)
        }
    });
};

const createAmfContractTerms = (contractTerms: IAmfContractTerms): AmfTierContractTerms => {
    return AmfTierContractTerms.clone({
        adRevShare: {
            revSharePercent: Number(contractTerms.adRevShare.revSharePercent),
            playTypes: contractTerms.adRevShare?.playTypes,
            conversions: contractTerms.adRevShare?.conversions?.map((conversion, index) => ({
                ...conversion,
                orderId: index + 1
            }))
        }
    });
};

const saveContractTerms = async (props: PrimeAMFRateCardSaveProps): Promise<string> => {
    return new Promise<string>((resolve) => {
        resolve('');
    });

    // Comment out SetPrime/AmfContractTerms API call for futre reference as team decides to move contract terms save to Core Rates step
    /*
    const { contractId, values, user, metadata } = props;
    const services = Object.values(metadata.serviceMapping);
    const isPrimeRateCard = values.serviceId === services[2].serviceId;

    try {
        const input = isPrimeRateCard
            ? SetPrimeContractTermsInput.clone({
                  contractId: contractId,
                  contractTerms: createPrimeContractTerms(values.primeContractTerms!),
                  changedBy: user
              })
            : SetAmfContractTermsInput.clone({
                  contractId: contractId,
                  contractTerms: createAmfContractTerms(values.amfContractTerms!),
                  changedBy: user
              });

        if (isPrimeRateCard) {
            return new Promise<string>((resolve) => {
                contractConfigurationClient.setPrimeContractTerms(input).subscribe(
                    () => resolve(''),
                    (error: UnknownCoralError) => {
                        resolve(`setPrimeContractTerms unexpected error ${JSON.stringify(error.receivedException)}`);
                    }
                );
            });
        } else {
            return new Promise<string>((resolve) => {
                contractConfigurationClient.setAmfContractTerms(input).subscribe(
                    () => resolve(''),
                    (error: UnknownCoralError) => {
                        resolve(`setAmfContractTerms unexpected error ${JSON.stringify(error.receivedException)}`);
                    }
                );
            });
        }
    } catch (e) {
        console.error(e);
        return Promise.resolve(
            `Caught error while building ${
                isPrimeRateCard ? 'setPrimeContractTerms' : 'setAmfContractTerms'
            } payload: ${e}`
        );
    }
    */
};

const getTerritoryCode = (metadata: IMetadata, territoryId: number): string => {
    const territory = Object.values(metadata.territoryMapping).find(
        (territory) => territory.territoryId === territoryId
    );

    return territory!.territoryCode!;
};

const getCaeId = (metadata: IMetadata, caeCode: string): number => {
    const cae = Object.values(metadata.CaeMapping!).find((mapping) => mapping.contentAcquisitionEntityCode === caeCode);

    return cae!.contentAcquisitionEntityID!;
};

const getRoyaltyModelInfo = (
    metadata: IMetadata,
    serviceId: number,
    royaltyModelCode: RoyaltyModelsEnum
): Record<string, number> => {
    const services = Object.values(metadata.serviceMapping);
    let royaltyModelType: RoyaltyModelType;

    if (serviceId === services[2].serviceId) {
        if (royaltyModelCode === RoyaltyModelsEnum.AD_REV) {
            royaltyModelType = metadata.royaltyModelTypes!.find(
                (type) => type.royaltyModelTypeCode === RoyaltyModelTypesEnum.CPP_ADREV
            )!;
        } else {
            royaltyModelType = metadata.royaltyModelTypes!.find(
                (type) => type.royaltyModelTypeCode === royaltyModelCode
            )!;
        }
    } else if (serviceId === services[3].serviceId) {
        royaltyModelType = metadata.royaltyModelTypes!.find(
            (type) => type.royaltyModelTypeCode === RoyaltyModelTypesEnum.CPP_ADREV
        )!;
    }

    const royaltyModel = royaltyModelType!.royaltyModels!.find((model) => model.royaltyModelCode === royaltyModelCode);
    return {
        royaltyModelTypeId: royaltyModelType!.royaltyModelTypeId!,
        royaltyModelId: royaltyModel!.royaltyModelId!
    };
};

const createCoreRate = (
    row: IRateCardGridRowData,
    paperPlanId: number,
    royaltyModelInfo: Record<string, number>,
    programTypes: ProgramType[],
    rate: number,
    tieredRates?: TieredRates,
    tieredRatesList?: TieredRatesList
) => {
    return CoreRate.clone({
        territoryId: row.territoryId,
        paperPlanId,
        royaltyModelId: royaltyModelInfo.royaltyModelId,
        royaltyModelTypeId: royaltyModelInfo.royaltyModelTypeId,
        playTypeDefinitionId: row.playTypeDefinitionId ? Number(row.playTypeDefinitionId) : undefined,
        playTypeId: row.playTypeId ? Number(row.playTypeId) : undefined,
        programTypeId: programTypes.find((programType) => programType.programTypeName === row.programType)
            ?.programTypeId,
        rate: rate,
        currency: row.rateCardCurrency,
        tieredRates,
        tieredRatesList
    });
};

function checkNumber(name: string, num: any) {
    if (!isNumber(num) || isNaN(num) || isUndefined(num) || isNull(num)) {
        throw new Error(`Invalid value for ${name}: ${num}`);
    }
}

const createTierRates = (
    row: IRateCardGridRowData,
    cppConversionModels: RatchetModels,
    metadata: IMetadata,
    hasUmgConversionModel: boolean,
    hasWmgConversionModel: boolean
) => {
    let tiers, umgRateValue, tieredRatesList;

    // Convert UMG relative thresholds back to absolute thresholds using pre-defined territory specific benchmark
    if (hasUmgConversionModel) {
        const rowTerritoryCode = getTerritoryCode(metadata, row.territoryId);
        const sortedConversionModels = sortBy(cppConversionModels, ['orderId']);

        // Territory benchmark value only belongs in PRIME_CONVERSION_UMG and PRIME_CONVERSION_FLOOR_UMG
        const territoryBenchmarkValue = sortedConversionModels[0].ratchetDefinitions?.find(
            (ratchetDefinition) =>
                ratchetDefinition.territory === rowTerritoryCode &&
                ratchetDefinition.type === RatchetDefinitionsEnum.BENCHMARK
        )?.value;
        const territoryBenchmark = !isUndefined(territoryBenchmarkValue) ? Number(territoryBenchmarkValue) : undefined;
        checkNumber('territoryBenchmark', territoryBenchmark);

        tieredRatesList = sortedConversionModels.map((conversion, conversionModelIndex) => {
            const umgRatchets = conversionModelsThresholdsMap[conversion.ratchetModel!];
            const tiers: RateTiers | undefined = umgRatchets
                ?.filter((ratchet) => ratchet.thresholdOperator !== '><')
                .map((ratchet, ratchetIndex) => {
                    const threshold = Number((territoryBenchmark! + ratchet.threshold!).toFixed(2));
                    checkNumber('threshold', threshold);

                    const rate = Number(row[getThresholdFieldName(ratchetIndex + 1)]);
                    checkNumber('rate', rate);

                    return {
                        threshold: threshold < 0 ? 0 : threshold,
                        thresholdOperator: '<',
                        rate: rate
                    };
                });

            return {
                orderId: conversionModelIndex + 1,
                ratchetModelId: conversion!.ratchetModelId!,
                tiers: tiers!
            };
        });

        // Use rate of first conversion model
        umgRateValue = Number(
            row[getThresholdFieldName(conversionModelsThresholdsMap[sortedConversionModels[0].ratchetModel!].length)]
        );
    }
    // Build tiered rates input for WMG Primary/Secondary/Tertiary ratchet model
    else if (hasWmgConversionModel) {
        tieredRatesList = sortBy(cppConversionModels, ['orderId']).map((conversion, index) => {
            if (conversion.ratchetModel !== RatchetModelsEnum.PRIME_TERTIARY_CONVERSION_WMG) {
                return {
                    orderId: index + 1,
                    ratchetModelId: conversion!.ratchetModelId!,
                    tiers: conversion!.ratchets!.map((ratchet) => ({
                        threshold: ratchet.threshold!,
                        thresholdOperator: ratchet.thresholdOperator!,
                        rate: Number(row[getThresholdFieldName(ratchet.threshold!)])
                    }))
                };
            } else {
                return {
                    orderId: index + 1,
                    ratchetModelId: conversion!.ratchetModelId!,
                    tiers: conversion!.ratchets!.map((ratchet) => ({
                        threshold: ratchet.threshold!,
                        thresholdOperator: ratchet.thresholdOperator!,
                        rate: Number(row[getThresholdFieldName(ratchet.threshold!, wmgBlendedRateGridColIdSuffix)])
                    }))
                };
            }
        });
    } else if (cppConversionModels.length > 0) {
        const conversion = cppConversionModels[0];
        // reset tiers to be undefined if the tier/threshold rate value is empty, which is the DE Radio Play case
        tiers = conversion?.ratchets
            ?.filter(
                (ratchet) =>
                    !isNaN(Number(row[getThresholdFieldName(ratchet.threshold!)])) &&
                    !isEmpty(row[getThresholdFieldName(ratchet.threshold!)])
            )
            .map((ratchet) => ({
                threshold: ratchet.threshold!,
                thresholdOperator: ratchet.thresholdOperator!,
                rate: Number(row[getThresholdFieldName(ratchet.threshold!)])
            }));

        if (isEmpty(tiers)) tiers = undefined;
    }

    return { tiers, umgRateValue, tieredRatesList };
};

const saveCoreRates = async (props: PrimeAMFRateCardSaveProps): Promise<string> => {
    const { contractId, values, user, metadata, customerTypes, programTypes, conversionModels } = props;

    const services = Object.values(metadata.serviceMapping);
    const isPrimeRateCard = values.serviceId === services[2].serviceId;

    // Filter out CPP rows with empty rates before processing
    const filteredValues = {
        ...values,
        rateCardRowData: {
            ...values.rateCardRowData,
            cpp: values.rateCardRowData.cpp.filter((row) => row.rate && row.rate.trim() !== '')
        }
    };

    let input: ModifyCoreRatesInput;
    try {
        // Create territory terms
        let territoryTerms: TerritoryTermInput[] = filteredValues.rateCardRowData.cpp.map((row) =>
            TerritoryTermInput.clone({
                territoryId: row.territoryId,
                contentAcquisitionEntityId: getCaeId(metadata, row.cae),
                settlementCurrency: row.settlementCurrency
            })
        );

        // Add India territoryTerm if MAU model is toggled on
        if (!isEmpty(values.rateCardRowData.mau)) {
            territoryTerms.push(
                ...values.rateCardRowData.mau.map((row) =>
                    TerritoryTermInput.clone({
                        territoryId: row.territoryId,
                        contentAcquisitionEntityId: getCaeId(metadata, row.cae),
                        settlementCurrency: row.settlementCurrency
                    })
                )
            );
        }

        territoryTerms = uniqBy(territoryTerms, 'territoryId');

        const coreRates: CoreRate[] = [];

        // Prime rate card
        if (isPrimeRateCard) {
            const cppRoyaltyModelInfo = getRoyaltyModelInfo(metadata, values.serviceId, RoyaltyModelsEnum.CPP);

            const cppConversionModels = values.primeContractTerms?.costPerPlay?.conversions;

            const revShareValue = values.primeContractTerms?.adRevShare?.revSharePercent;
            const adRevShareTerritories = values.primeContractTerms?.adRevShare?.territories;

            // Prime CPP Core rates
            const cppRates = values.primeContractTerms?.costPerPlay?.customerTypes?.flatMap((customerType) => {
                const paperPlanId = customerTypes.find(
                    (paperPlanMetadata) => paperPlanMetadata.paperPlan?.paperPlanName === customerType
                )!.paperPlan!.paperPlanId!;

                return filteredValues.rateCardRowData.cpp.map((row) => {
                    const conversion = cppConversionModels?.[0];
                    const hasUmgConversionModel =
                        !isUndefined(conversion) &&
                        [
                            RatchetModelsEnum.PRIME_CONVERSION_UMG,
                            ...COMBINED_PRIME_UMG_FLOOR_CONVERSION_MODELS
                        ].includes(conversion.ratchetModel as RatchetModelsEnum);
                    const hasWmgConversionModel =
                        !isUndefined(conversion) &&
                        sortBy(cppConversionModels, ['orderId'])?.[0].ratchetModel ===
                            RatchetModelsEnum.PRIME_PRIMARY_CONVERSION_WMG;

                    const { tiers, umgRateValue, tieredRatesList } = createTierRates(
                        row,
                        cppConversionModels ?? [],
                        metadata,
                        hasUmgConversionModel,
                        hasWmgConversionModel
                    );

                    let currentCppRoyaltyModelInfo = cppRoyaltyModelInfo;
                    if (adRevShareTerritories?.includes(getTerritoryCode(metadata, row.territoryId))) {
                        currentCppRoyaltyModelInfo = getRoyaltyModelInfo(metadata, 3, RoyaltyModelsEnum.CPP);
                    }

                    return createCoreRate(
                        row,
                        paperPlanId,
                        currentCppRoyaltyModelInfo,
                        programTypes,
                        hasUmgConversionModel ? umgRateValue! : parseFloat(row.rate),
                        isUndefined(tiers)
                            ? undefined
                            : {
                                  ratchetModelId: conversion!.ratchetModelId!,
                                  tiers: tiers
                              },
                        tieredRatesList
                    );
                });
            });

            if (!isUndefined(cppRates)) coreRates.push(...cppRates);

            // Prime MAU Core rates
            const mauRoyaltyModelInfo = getRoyaltyModelInfo(metadata, values.serviceId, RoyaltyModelsEnum.MAU);

            const mauRates = values.rateCardRowData.mau.map((row) => {
                const paperPlanId = customerTypes.find(
                    (paperPlanMetadata) => paperPlanMetadata.paperPlan?.paperPlanName === row.paperPlanName
                )!.paperPlan!.paperPlanId!;
                return createCoreRate(row, paperPlanId, mauRoyaltyModelInfo, programTypes, parseFloat(row.rate));
            });

            if (!isUndefined(mauRates)) coreRates.push(...mauRates);

            // Prime ADREV Core rates
            if (!isUndefined(revShareValue)) {
                const adRevRoyaltyModelInfo = getRoyaltyModelInfo(metadata, values.serviceId, RoyaltyModelsEnum.AD_REV);

                const territoryCodesList = values.primeContractTerms?.adRevShare?.territories || [];

                const adRevRates = values.primeContractTerms?.costPerPlay?.customerTypes?.flatMap((customerType) => {
                    const paperPlanId = customerTypes.find(
                        (paperPlanMetadata) => paperPlanMetadata.paperPlan?.paperPlanName === customerType
                    )!.paperPlan!.paperPlanId!;

                    return filteredValues.rateCardRowData.cpp
                        .filter((row) => {
                            const territoryCode = getTerritoryCode(metadata, row.territoryId);
                            return territoryCodesList.includes(territoryCode);
                        })
                        .map((row) =>
                            createCoreRate(
                                row,
                                paperPlanId,
                                adRevRoyaltyModelInfo,
                                programTypes,
                                parseFloat(revShareValue)
                            )
                        );
                });

                if (!isUndefined(adRevRates)) coreRates.push(...adRevRates);
            }
        }
        // AMF rate card
        else {
            // AMF CPP Core rates
            const cppRoyaltyModelInfo = getRoyaltyModelInfo(metadata, values.serviceId, RoyaltyModelsEnum.CPP);

            const cppConversionModels = values.amfContractTerms?.adRevShare?.conversions;

            const cppRates = filteredValues.rateCardRowData.cpp.map((row) => {
                const conversion = cppConversionModels?.[0];
                const tiers = conversion?.ratchets?.map((ratchet) => ({
                    threshold: ratchet.threshold!,
                    thresholdOperator: ratchet.thresholdOperator!,
                    rate: Number(row[getThresholdFieldName(ratchet.threshold!)])
                }));

                const paperPlanId = customerTypes.find(
                    (paperPlanMetadata) => paperPlanMetadata.paperPlan?.serviceId === values.serviceId
                )!.paperPlan!.paperPlanId!;

                return createCoreRate(
                    row,
                    paperPlanId,
                    cppRoyaltyModelInfo,
                    programTypes,
                    parseFloat(row.rate),
                    isUndefined(tiers)
                        ? undefined
                        : {
                              ratchetModelId: conversion!.ratchetModelId!,
                              tiers: tiers
                          }
                );
            });

            if (!isUndefined(cppRates)) coreRates.push(...cppRates);

            // AMF ADREV Core rates
            const revShareValue = values.amfContractTerms?.adRevShare.revSharePercent;
            if (!isUndefined(revShareValue)) {
                const revShareRoyaltyModelInfo = getRoyaltyModelInfo(
                    metadata,
                    values.serviceId,
                    RoyaltyModelsEnum.AD_REV
                );
                const conversion = cppConversionModels?.[0];
                const hasUmgConversionModel =
                    conversion && conversion.ratchetModel === RatchetModelsEnum.AMF_CONVERSION_UMG;
                const umg52PercentRevShareConversionModel = conversionModels.find(
                    (conversion) => conversion.ratchetModel === RatchetModelsEnum.AMF_REVSHARE_CONVERSION_UMG
                );
                const paperPlanId = customerTypes.find(
                    (paperPlanMetadata) => paperPlanMetadata.paperPlan?.serviceId === values.serviceId
                )!.paperPlan!.paperPlanId!;

                const revShareRates = filteredValues.rateCardRowData.cpp.map((row) =>
                    createCoreRate(
                        row,
                        paperPlanId,
                        revShareRoyaltyModelInfo,
                        programTypes,
                        parseFloat(revShareValue),
                        hasUmgConversionModel
                            ? {
                                  ratchetModelId: umg52PercentRevShareConversionModel!.ratchetModelId!,
                                  tiers: conversionModelsThresholdsMap[RatchetModelsEnum.AMF_REVSHARE_CONVERSION_UMG]
                              }
                            : undefined
                    )
                );

                coreRates.push(...revShareRates);
            }
        }

        input = ModifyCoreRatesInput.clone({
            contractId: contractId,
            requestedBy: user,
            territoryTerms: TerritoryTermInputs.fromJson(territoryTerms),
            rates: CoreRates.fromJson(coreRates),
            allowDeletion: true
        });
    } catch (e) {
        console.error(e);
        return Promise.resolve(`Caught error while building core rates payload: ${e}`);
    }

    // For Prime rate card, make ModifyCoreRates API call to save core rates with most terms data,
    // and a followup SetPrimeContractTerms API call to save PRS deduction cap value, program type definitions, and UMG guardrail flags
    if (isPrimeRateCard) {
        const modifyCoreRatesPromise = contractConfigurationClient.modifyCoreRates(input).toPromise();

        return modifyCoreRatesPromise.then(() => {
            return new Promise<string>((resolve) => {
                const programTypeDefinitionsToSave = values.primeContractTerms?.costPerPlay?.programTypeList ?? [];
                const ratchetModelDefinitionsToSave =
                    values.primeContractTerms?.costPerPlay?.conversions
                        ?.filter(
                            (conversion) =>
                                conversion.ratchetModel === RatchetModelsEnum.PRIME_CONVERSION_FLOOR_UMG_GUARDRAIL
                        )
                        .map((conversion) => ({
                            ...conversion,
                            ratchetDefinitions: conversion.ratchetDefinitions?.filter(
                                (definition) => definition.type === RatchetModelDefinitionEnum.UMG_CONVERSION_GUARDRAIL
                            )
                        })) ?? [];

                const primeContractTermsInput = SetPrimeContractTermsInput.clone({
                    contractId: contractId,
                    contractTerms: PrimeContractTerms.clone({
                        costPerPlay: {
                            playTypes: [],
                            programTypeList: programTypeDefinitionsToSave,
                            conversions: ratchetModelDefinitionsToSave
                        },
                        prsDeductionCap: {
                            type: values.primeContractTerms!.prsDeductionCap!.type,
                            value: Number(values.primeContractTerms!.prsDeductionCap!.value)
                        }
                    }),
                    changedBy: user
                });
                contractConfigurationClient.setPrimeContractTerms(primeContractTermsInput).subscribe(
                    () => resolve(''),
                    (error: UnknownCoralError) => {
                        resolve(`setPrimeContractTerms unexpected error ${JSON.stringify(error.receivedException)}`);
                    }
                );
            });
        });
    } else {
        return new Promise<string>((resolve) => {
            contractConfigurationClient.modifyCoreRates(input).subscribe(
                () => resolve(''),
                (error: UnknownCoralError) => {
                    resolve(`modifyCoreRates unexpected error ${JSON.stringify(error.receivedException)}`);
                }
            );
        });
    }
};

const publishContract = async (props: PrimeAMFRateCardSaveProps): Promise<string> => {
    const input = PublishContractInput.clone({
        contractId: props.contractId,
        changedBy: props.user
    });

    return new Promise<string>((resolve) => {
        contractConfigurationClient.publishContract(input).subscribe(
            () => resolve(''),
            (error: UnknownCoralError) =>
                resolve(
                    `Publishing contract encountered an unexpected error ${JSON.stringify(error.receivedException)}`
                )
        );
    });
};

export const primeAMFRateCardHandlers: Handlers<PrimeAMFRateCardSaveProps> = {
    [ROUTES.RATE_CARDS.STEP.CONTRACT_TERMS]: saveContractTerms,
    [ROUTES.RATE_CARDS.STEP.CORE_RATES]: saveCoreRates,
    [ROUTES.RATE_CARDS.STEP.REVIEW]: publishContract
};

export const primeAMFRateCardFullStepList = [ROUTES.RATE_CARDS.STEP.CONTRACT_TERMS, ROUTES.RATE_CARDS.STEP.CORE_RATES];
